import { FieldErrors, FormLabelRAC, GhostButton } from "@certn/cascadia-react";
import { _InputWrapper } from "@certn/cascadia-react/components/inputs/_assets/_InputWrapper";
import { slugifyPath } from "@certn/utils";
import { useContext } from "react";

import { checkIfDivisible, getDecimalPoints } from "./utils";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { Input } from "../components/Input";
import { NumberField } from "../components/NumberField";
import { SummaryItem } from "../components/SummaryItem";
import { Text } from "../components/Text";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

export function NumberFieldControl(props: CascadiaControlProps) {
  const { options, description, path } = props;

  const { isSummaryForPath, formID } = useContext(CascadiaJsonFormContext);

  if (isSummaryForPath?.(path)) {
    return (
      <SummaryItem label={props.label} labelAlign={options.labelAlign}>
        {props.data}
      </SummaryItem>
    );
  }

  const testIDPath = `${slugifyPath(props.path)}`;

  const multipleOf = props.schema.multipleOf || 1;
  const decimals = getDecimalPoints(multipleOf);
  const minimum = props.schema.minimum;

  return (
    <NumberField
      data-testid={
        formID
          ? `${formID}-${testIDPath}-number-field`
          : `${testIDPath}-number-field`
      }
      name={props.path}
      value={props.data}
      onChange={props.setValue}
      isRequired={props.required}
      isDisabled={!props.enabled}
      isInvalid={props.invalid}
      onBlur={props.setTouched}
      defaultValue={props.schema.default}
      minValue={options.minValue ? options.minValue : minimum}
      step={
        options.isSmallStep || !checkIfDivisible(1, multipleOf)
          ? multipleOf // Buttons change by multipleOf and snaps when typed
          : undefined // Buttons change by 1 while allowing floats be typed in
      }
      formatOptions={{
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
      }}
      labelAlign={options.labelAlign}
    >
      <FormLabelRAC info={options.info} isRequired={props.required}>
        {props.label}
      </FormLabelRAC>
      {description && (
        <Text style="input/helper" color="text-weak" slot="description">
          {description}
        </Text>
      )}
      <_InputWrapper>
        <GhostButton slot="decrement">-</GhostButton>
        <Input />
        <GhostButton slot="increment">+</GhostButton>
      </_InputWrapper>

      {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
    </NumberField>
  );
}

NumberFieldControl.displayName = "NumberFieldControl";

export const NumberFieldRenderer = withCascadiaControlProps(NumberFieldControl);
